/**
 * PortfolioPage.tsx — 2004va
 *
 * Creator's career record. Shows:
 * - Stats: total shots, grade distribution, total earnings, license count
 * - Shoot sessions: each session as a unit with shot count + earnings
 * - Recent shots with grade, score, held/routed status
 *
 * Images served from Cloudinary URL (thumbnail) — no storage cost.
 * All data from Supabase — no external calls.
 */

import { useQuery } from '@tanstack/react-query'
import { Camera, Zap, TrendingUp, Award, Clock, Lock } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { ChannelSetup } from '@/components/publish/PublishSheet'
import { SHOOT_TYPE_LABELS, type ShootType } from '@/stores/sessionStore'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'

// ── Types ─────────────────────────────────────────────────────────────────────

interface SessionStat {
  id:             string
  event_name:     string | null
  venue_name:     string | null
  confirmed_type: ShootType
  session_brief:  string | null
  media_count:    number
  started_at:     string
  earnings:       number
  licenses:       number
}

interface GradeDist {
  A: number; B: number; C: number; D: number; total: number
}

interface PortfolioStats {
  total_shots:    number
  total_earnings: number
  total_licenses: number
  grade_dist:     GradeDist
  top_mood:       string
  sessions:       SessionStat[]
  recent_media:   RecentMedia[]
}

interface RecentMedia {
  id:             string
  cloudinary_url: string
  thumbnail_url:  string | null
  ai_grade:       string | null
  ai_route_score: number | null
  ai_mood:        string | null
  ai_editor_note: string | null
  status:         string
  challenge_status: string | null
  created_at:     string
  event_name:     string | null
}

// ── Data fetching ─────────────────────────────────────────────────────────────

async function fetchPortfolio(participantId: string): Promise<PortfolioStats> {
  // Parallel fetches
  const [mediaRes, sessionsRes, licensesRes] = await Promise.all([
    supabase
      .from('media_items')
      .select('id, cloudinary_url, thumbnail_url, ai_grade, ai_route_score, ai_mood, ai_editor_note, status, challenge_status, created_at, event:club_events(name)')
      .eq('participant_id', participantId)
      .order('created_at', { ascending: false })
      .limit(50),

    supabase
      .from('shoot_sessions')
      .select('id, event_id, confirmed_type, session_brief, media_count, started_at, event:club_events(name, venue)')
      .eq('participant_id', participantId)
      .order('started_at', { ascending: false })
      .limit(20),

    supabase
      .from('license_requests')
      .select('id, price_gbp, decided_at')
      .eq('creator_id', participantId)
      .eq('status', 'approved'),
  ])

  const media    = mediaRes.data    ?? []
  const sessions = sessionsRes.data ?? []
  const licenses = licensesRes.data ?? []

  // Grade distribution
  const grade_dist: GradeDist = { A: 0, B: 0, C: 0, D: 0, total: media.length }
  media.forEach(m => {
    if (m.ai_grade && m.ai_grade in grade_dist) {
      (grade_dist as unknown as Record<string, number>)[m.ai_grade]++
    }
  })

  // Top mood
  const moodMap: Record<string, number> = {}
  media.forEach(m => { if (m.ai_mood) moodMap[m.ai_mood] = (moodMap[m.ai_mood] ?? 0) + 1 })
  const top_mood = Object.entries(moodMap).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—'

  // Earnings
  const total_earnings = licenses.reduce((sum, l) => sum + (l.price_gbp ?? 0), 0)

  // Map sessions with earnings per session (simplified — earnings per session not tracked yet, show totals)
  const sessionStats: SessionStat[] = sessions.map(s => ({
    id:             s.id,
    event_name:     (s.event as { name?: string } | null)?.name ?? null,
    venue_name:     (s.event as { venue?: string } | null)?.venue ?? null,
    confirmed_type: (s.confirmed_type as ShootType) ?? 'general',
    session_brief:  s.session_brief,
    media_count:    s.media_count ?? 0,
    started_at:     s.started_at,
    earnings:       0,   // TODO: join to licenses via session when that link is built
    licenses:       0,
  }))

  const recent_media: RecentMedia[] = media.slice(0, 24).map(m => ({
    id:               m.id,
    cloudinary_url:   m.cloudinary_url,
    thumbnail_url:    m.thumbnail_url,
    ai_grade:         m.ai_grade,
    ai_route_score:   m.ai_route_score,
    ai_mood:          m.ai_mood,
    ai_editor_note:   m.ai_editor_note,
    status:           m.status,
    challenge_status: m.challenge_status,
    created_at:       m.created_at,
    event_name:       (m.event as { name?: string } | null)?.name ?? null,
  }))

  return {
    total_shots:    media.length,
    total_earnings,
    total_licenses: licenses.length,
    grade_dist,
    top_mood,
    sessions:       sessionStats,
    recent_media,
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const GRADE_STYLE: Record<string, string> = {
  A: 'bg-emerald-900/60 text-emerald-300 border-emerald-700/40',
  B: 'bg-blue-900/60 text-blue-300 border-blue-700/40',
  C: 'bg-amber-900/60 text-amber-300 border-amber-700/40',
  D: 'bg-red-900/60 text-red-300 border-red-700/40',
}

function thumbnailUrl(cloudinaryUrl: string): string {
  // Cloudinary transformation — 400px wide, quality 70, auto format
  return cloudinaryUrl.replace('/upload/', '/upload/w_400,q_70,f_auto/')
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PortfolioPage() {
  const { participant } = useAuthStore()

  const { data: portfolio, isLoading, error } = useQuery<PortfolioStats>({
    queryKey: ['portfolio', participant?.id],
    queryFn:  () => fetchPortfolio(participant!.id),
    enabled:  !!participant,
    staleTime: 60_000,
  })

  if (isLoading) return (
    <div className="flex justify-center py-16"><Spinner size={28} /></div>
  )

  if (error || !portfolio) return (
    <div className="text-center py-12">
      <p className="text-room-muted text-sm">Could not load portfolio</p>
    </div>
  )

  const { total_shots, total_earnings, total_licenses, grade_dist, top_mood, sessions, recent_media } = portfolio

  return (
    <div className="space-y-8 animate-fade-in pb-4">

      {/* ── Stats row ── */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={<Camera size={16} />} label="Shots" value={total_shots.toString()} />
        <StatCard icon={<Award size={16} />}  label="Licensed" value={total_licenses.toString()} />
        <StatCard
          icon={<TrendingUp size={16} />}
          label="Earned"
          value={total_earnings > 0 ? `£${total_earnings.toFixed(0)}` : '£0'}
          sub="after 5% fee"
        />
        <StatCard icon={<Zap size={16} />} label="Top mood" value={top_mood} />
      </div>

      {/* ── Founder badge ── */}
      {participant?.founder_access && (
        <div className="flex items-center gap-2 bg-room-accent/8 border border-room-accent/20 rounded-xl px-4 py-2.5">
          <span className="w-1.5 h-1.5 rounded-full bg-room-accent flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold text-room-accent">Founder access</span>
            {participant.founder_since && (
              <span className="text-room-muted text-xs ml-2">
                since {new Date(participant.founder_since).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
          <span className="text-room-muted/60 text-[10px]">Recognised permanently</span>
        </div>
      )}

      {/* ── Grade distribution ── */}
      {grade_dist.total > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-room-muted mb-3">
            Grade breakdown
          </h3>
          <div className="space-y-2">
            {(['A','B','C','D'] as const).map(g => {
              const count = grade_dist[g]
              const pct   = grade_dist.total > 0 ? (count / grade_dist.total) * 100 : 0
              return (
                <div key={g} className="flex items-center gap-3">
                  <span className={cn(
                    'text-xs font-black px-1.5 py-0.5 rounded border w-8 text-center flex-shrink-0',
                    GRADE_STYLE[g]
                  )}>
                    {g}
                  </span>
                  <div className="flex-1 bg-room-border rounded-full h-1.5 overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        g === 'A' ? 'bg-emerald-400' :
                        g === 'B' ? 'bg-blue-400' :
                        g === 'C' ? 'bg-amber-400' : 'bg-red-400'
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-room-muted w-8 text-right flex-shrink-0">{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Sessions ── */}
      {sessions.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-room-muted mb-3">
            Shoot sessions
          </h3>
          <div className="space-y-2">
            {sessions.map(s => (
              <div key={s.id}
                className="bg-room-surface border border-room-border rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-room-accent/10 flex items-center justify-center flex-shrink-0">
                  <Camera size={14} className="text-room-accent" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-semibold truncate">
                    {s.event_name ?? 'Personal shoot'}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-room-muted">
                      {SHOOT_TYPE_LABELS[s.confirmed_type] ?? s.confirmed_type}
                    </span>
                    {s.session_brief && (
                      <span className="text-[10px] text-room-muted/60 truncate">· {s.session_brief}</span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-white text-xs font-semibold">{s.media_count} shots</p>
                  <p className="text-room-muted text-[10px] flex items-center gap-1 justify-end">
                    <Clock size={9} />
                    {formatDate(s.started_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Recent shots grid ── */}
      {recent_media.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-room-muted mb-3">
            Recent shots
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {recent_media.map(m => (
              <div key={m.id} className="relative aspect-square rounded-xl overflow-hidden bg-room-surface border border-room-border group">
                <img
                  src={thumbnailUrl(m.cloudinary_url)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />

                {/* Grade badge */}
                {m.ai_grade && (
                  <span className={cn(
                    'absolute top-1.5 left-1.5 text-[10px] font-black px-1.5 py-0.5 rounded border',
                    GRADE_STYLE[m.ai_grade] ?? GRADE_STYLE.D
                  )}>
                    {m.ai_grade}
                  </span>
                )}

                {/* Held overlay */}
                {m.status === 'held' && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-1">
                      <Lock size={14} className="text-amber-300" />
                      <span className="text-[9px] text-amber-300 font-semibold">Held</span>
                    </div>
                  </div>
                )}

                {/* Challenge pending badge */}
                {m.challenge_status === 'pending' && (
                  <span className="absolute bottom-1.5 right-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-purple-900/80 text-purple-300 border border-purple-700/40">
                    Review
                  </span>
                )}

                {/* Score on hover */}
                {m.ai_route_score != null && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[10px] text-white flex items-center gap-1">
                      <Zap size={8} />
                      {Math.round(m.ai_route_score * 100)}%
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Channel setup ── */}
      {participant && (
        <div className="bg-room-surface border border-room-border rounded-2xl p-4">
          <ChannelSetup participantId={participant.id} />
        </div>
      )}

      {total_shots === 0 && (
        <div className="text-center py-16 space-y-2">
          <Camera size={32} className="text-room-muted mx-auto" />
          <p className="text-room-muted text-sm">No shots yet</p>
          <p className="text-room-muted/60 text-xs">Start a shoot to build your portfolio</p>
        </div>
      )}
    </div>
  )
}

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub }: {
  icon:  React.ReactNode
  label: string
  value: string
  sub?:  string
}) {
  return (
    <div className="bg-room-surface border border-room-border rounded-xl px-4 py-3 space-y-1">
      <div className="flex items-center gap-1.5 text-room-muted">
        {icon}
        <span className="text-xs font-semibold uppercase tracking-widest">{label}</span>
      </div>
      <p className="text-white text-xl font-black">{value}</p>
      {sub && <p className="text-room-muted text-[10px]">{sub}</p>}
    </div>
  )
}
